import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlatformFeatures = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: 1,
      title: "Smart Collection Management",
      description: "Organize and track your collectibles with AI-powered categorization, detailed condition tracking, and automated valuation updates.",
      icon: "Database",
      benefits: [
        "AI-powered item recognition and categorization",
        "Detailed condition and provenance tracking",
        "Automated market value updates",
        "Custom collection organization tools"
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
    },
    {
      id: 2,
      title: "Real-Time Market Intelligence",
      description: "Stay ahead with live auction data, price trends, and market analysis powered by machine learning algorithms.",
      icon: "TrendingUp",
      benefits: [
        "Live auction tracking and alerts",
        "Predictive price trend analysis",
        "Market timing recommendations",
        "Comparative market analysis tools"
      ],
      image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop"
    },
    {
      id: 3,
      title: "Authentication Network",
      description: "Connect with certified experts and use advanced verification tools to ensure authenticity and protect your investments.",
      icon: "Shield",
      benefits: [
        "Expert authentication services",
        "Blockchain-based provenance tracking",
        "Fraud detection algorithms",
        "Certificate management system"
      ],
      image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=600&h=400&fit=crop"
    },
    {
      id: 4,
      title: "Portfolio Analytics",
      description: "Comprehensive performance tracking with detailed analytics, ROI calculations, and investment insights.",
      icon: "BarChart3",
      benefits: [
        "Advanced portfolio performance metrics",
        "ROI tracking and projections",
        "Risk assessment tools",
        "Tax reporting assistance"
      ],
      image: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&h=400&fit=crop"
    }
  ];

  const currentFeature = features?.[activeFeature];

  return (
    <section className="py-24 bg-gradient-to-b from-muted/10 to-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Powerful Features for Serious Collectors
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to manage, analyze, and grow your collection with professional-grade tools and insights.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Feature Navigation */}
          <div className="space-y-6">
            <div className="space-y-4">
              {features?.map((feature, index) => (
                <motion.button
                  key={feature?.id}
                  onClick={() => setActiveFeature(index)}
                  className={`w-full text-left p-6 rounded-2xl transition-all duration-300 premium-hover ${
                    index === activeFeature
                      ? 'bg-primary/10 border border-primary/20 shadow-gold'
                      : 'bg-card border border-border hover:bg-muted/30'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      index === activeFeature
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={feature?.icon} size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-2 ${
                        index === activeFeature ? 'text-primary' : 'text-card-foreground'
                      }`}>
                        {feature?.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {feature?.description}
                      </p>
                    </div>
                    <Icon 
                      name="ChevronRight" 
                      size={20} 
                      className={`flex-shrink-0 transition-transform duration-300 ${
                        index === activeFeature ? 'rotate-90 text-primary' : 'text-muted-foreground'
                      }`}
                    />
                  </div>
                </motion.button>
              ))}
            </div>

            {/* CTA */}
            <div className="pt-6">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="w-full liquid-gold premium-hover"
                onClick={() => window.location.href = '/registration'}
              >
                Start Building Your Collection
              </Button>
            </div>
          </div>

          {/* Feature Showcase */}
          <motion.div
            key={activeFeature}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Feature Image */}
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={currentFeature?.image}
                alt={currentFeature?.title}
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                    <Icon name={currentFeature?.icon} size={20} color="#1A1B23" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {currentFeature?.title}
                  </h3>
                </div>
              </div>
            </div>

            {/* Feature Benefits */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-card-foreground mb-4">
                Key Benefits
              </h4>
              <div className="space-y-3">
                {currentFeature?.benefits?.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={14} className="text-success" />
                    </div>
                    <span className="text-muted-foreground">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Feature Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-2xl font-bold text-primary mb-1">99.8%</div>
                <div className="text-xs text-muted-foreground">Accuracy</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-2xl font-bold text-primary mb-1">&lt;1s</div>
                <div className="text-xs text-muted-foreground">Response</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                <div className="text-xs text-muted-foreground">Monitoring</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PlatformFeatures;